import React from 'react';
import { useTranslation } from 'react-i18next';
import { useZoneStatus } from '../hooks/useZoneStatus';
import TcButton from './TcButton';

interface ZoneStatusProps {
    zoneId: string;
    zoneName?: string;
    showActions?: boolean;
    className?: string;
    onClaimed?: () => void;
}

// Helper function to safely convert any value to a string
const getSafeErrorString = (error: any): string => {
    if (typeof error === 'string') {
        return error;
    }
    
    if (error && typeof error === 'object') {
        // Handle API error objects
        if (error.message && typeof error.message === 'string') {
            return error.message;
        }
        
        // Handle other error formats
        if (error.statusText && typeof error.statusText === 'string') {
            return error.statusText;
        }
        
        // Log warning for debugging
        console.warn('⚠️ ZoneStatus received error object instead of string:', error);
        
        // Fallback for unknown error objects
        return error.toString ? error.toString() : 'An error occurred';
    }
    
    if (error === null || error === undefined) {
        return 'An error occurred';
    }
    
    // Convert any other type to string
    return String(error);
};

export default function ZoneStatus({ 
    zoneId, 
    zoneName, 
    showActions = true, 
    className = '',
    onClaimed
}: ZoneStatusProps) {
    const { t } = useTranslation();
    const {
        zoneStatus,
        isLoading,
        error,
        isClaiming,
        isAvailable,
        isProcessing,
        isError,
        fetchZoneStatus,
        claimZone,
        clearError,
    } = useZoneStatus(zoneId);

    // Ensure error is always a string
    const safeError = error ? getSafeErrorString(error) : null;

    const handleClaimZone = async () => {
        try {
            const result = await claimZone() as any;
            if (result.type === 'zones/claimZoneStatus/fulfilled') {
                console.log('✅ Zone claimed successfully');
                onClaimed?.();
            }
        } catch (error) {
            console.error('❌ Failed to claim zone:', error);
        }
    };

    const handleRetry = () => {
        clearError();
        fetchZoneStatus();
    };

    // Status color classes
    const getStatusColor = () => {
        if (safeError) return 'text-red-400';
        if (isLoading) return 'text-yellow-400';
        if (isAvailable) return 'text-green-400';
        if (isProcessing) return 'text-blue-400';
        if (isError) return 'text-red-400';
        return 'text-gray-400';
    };

    const getStatusText = () => {
        if (safeError) return t('zones.error');
        if (isLoading) return t('zones.loading');
        if (isAvailable) return t('zones.available');
        if (isProcessing) return t('zones.processing');
        if (isError) return t('zones.errorStatus');
        return t('zones.unknown');
    };

    const getStatusIcon = () => {
        if (safeError) return '❌';
        if (isLoading) return '⏳';
        if (isAvailable) return '✅';
        if (isProcessing) return '🔄';
        if (isError) return '⚠️';
        return '❓';
    };

    return (
        <div className={`zone-status-container ${className}`}>
            <div className="zone-status-header flex items-center justify-between gap-4 mb-4">
                <div className="zone-info">
                    <h3 className="text-lg font-semibold text-white">
                        {zoneName || `Zone ${zoneId}`}
                    </h3>
                    <div className={`zone-status flex items-center gap-2 ${getStatusColor()}`}>
                        <span className="status-icon">{getStatusIcon()}</span>
                        <span className="status-text">{getStatusText()}</span>
                    </div>
                </div>
                
                {safeError && (
                    <TcButton
                        handleClick={handleRetry}
                        classNameStyle="retry-button max-w-40 mr-0"
                        hollow={true}
                    >
                        {t('common.retry')}
                    </TcButton>
                )}
            </div>

            {/* Zone details */}
            {zoneStatus && (
                <div className="zone-details text-left bg-gray-800 p-4 rounded-lg mb-4">
                    <div className="flex flex-col gap-2 text-sm">
                        <div>
                            <span className="text-gray-400">{t('zones.zoneId')}:</span>
                            <span className="text-white ml-2">{zoneStatus.zoneId}</span>
                        </div>
                        {zoneStatus.claimedBy && (
                            <div>
                                <span className="text-gray-400">{t('zones.claimedBy')}: </span>
                                <br />
                                <span className="text-white">{zoneStatus.claimedBy}</span>
                            </div>
                        )}
                        <div>
                            <span className="text-gray-400">{t('zones.lastUpdated')}:</span>
                            <span className="text-white ml-2">
                                {zoneStatus.updatedAt ? new Date(zoneStatus.updatedAt).toLocaleString() : 'Unknown'}
                            </span>
                        </div>
                        {zoneStatus.payload && (
                            <div className="col-span-2">
                                <span className="text-gray-400">{t('zones.payload')}:</span>
                                <pre className="text-white text-xs mt-2 bg-gray-900 p-2 rounded overflow-auto">
                                    {JSON.stringify(zoneStatus.payload, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Error display */}
            {safeError && (
                <div className="error-message !bg-red-900/50 border border-red-500 p-4 rounded-lg mb-4">
                    <div className="flex items-center gap-2">
                        <span className="text-red-400">❌</span>
                        <span className="text-red-300">{safeError}</span>
                    </div>
                </div>
            )}

            {/* Actions */}
            {showActions && isAvailable && !safeError && (
                <div className="zone-actions">
                    <TcButton
                        handleClick={handleClaimZone}
                        disabled={isClaiming}
                        classNameStyle="claim-button w-full"
                        primary={true}
                    >
                        {isClaiming ? t('zones.claiming') : t('zones.claimZone')}
                    </TcButton>
                </div>
            )}

            {/* Processing state message */}
            {isProcessing && (
                <div className="processing-message !bg-blue-900/50 border border-blue-500 p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                        <span className="text-blue-400">🔄</span>
                        <span className="text-blue-300">{t('zones.processingMessage')}</span>
                    </div>
                </div>
            )}
        </div>
    );
} 